import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import {
  FiArrowLeft,
  FiBox,
  FiCpu,
  FiGitBranch,
  FiLayers,
  FiShare2,
  FiTag,
} from "react-icons/fi"
import { previewDocumentPdf } from "../../../api/documentPdf"
import {
  type ProductionOrderResponseDTO,
  ProductionOrderService,
  type ProductResponceDTO,
  ProductService,
  WareHouseService,
} from "../../../client"
import PrintLanguageSelect from "../shared/PrintLanguageSelect"
import { BarcodeSvg } from "./barcode"

type MeldunekSpecificationPageProps = {
  orderId: string
  meldunekId: string
}

export default function MeldunekSpecificationPage({
  orderId,
  meldunekId,
}: MeldunekSpecificationPageProps) {
  const router = useRouter()
  const toast = useToast()

  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const mutedText = useColorModeValue("gray.600", "gray.400")
  const barcodeBg = useColorModeValue("white", "gray.900")
  const grayBg = useColorModeValue("gray.50", "gray.900")
  const greenBg = useColorModeValue("green.50", "gray.900")

  const { data: order, isLoading: orderLoading } =
    useQuery<ProductionOrderResponseDTO>({
      queryKey: ["production-order", orderId],
      queryFn: () => ProductionOrderService.getProductionOrderByID(orderId),
      enabled: !!orderId,
    })

  const { data: meldunkiData, isLoading: meldunkiLoading } = useQuery({
    queryKey: ["production-order-meldunki", orderId],
    queryFn: () => ProductionOrderService.getMeldunkiByOrder(orderId, 1, 1000),
    enabled: !!orderId,
  })

  const meldunek = (meldunkiData?.items ?? []).find(
    (m: any) => m.id === meldunekId,
  )

  const outputProductId =
    meldunek?.output_product_id ?? order?.output_product_id

  const { data: product } = useQuery<ProductResponceDTO>({
    queryKey: ["product", outputProductId],
    queryFn: () => ProductService.getProductByID(outputProductId!),
    enabled: !!outputProductId,
  })

  const { data: allProducts = [] } = useQuery({
    queryKey: ["products-all"],
    queryFn: () => ProductService.getAllProducts(),
  })

  const { data: allWarehouses = [] } = useQuery({
    queryKey: ["warehouses-all"],
    queryFn: () => WareHouseService.getAllWarehouses(),
  })

  const productMap = useMemoMap(allProducts)
  const warehouseMap = useMemoWarehouseMap(allWarehouses)

  const handlePrintPdfPL = async () => {
    try {
      await previewDocumentPdf(
        `/v1/production-orders/${orderId}/meldunki/${meldunekId}/pdf`,
        `Karta_Partii_${meldunek?.number ?? "meldunek"}.pdf`,
        "pl",
      )
    } catch (err: any) {
      toast({
        title: "Błąd generowania PDF",
        description: err?.message ?? "",
        status: "error",
      })
    }
  }

  const handlePrintPdfEN = async () => {
    try {
      await previewDocumentPdf(
        `/v1/production-orders/${orderId}/meldunki/${meldunekId}/pdf`,
        `Karta_Partii_${meldunek?.number ?? "meldunek"}.pdf`,
        "en",
      )
    } catch (err: any) {
      toast({
        title: "Błąd generowania PDF",
        description: err?.message ?? "",
        status: "error",
      })
    }
  }

  if (orderLoading || meldunkiLoading) {
    return (
      <Flex justify="center" align="center" minH="300px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    )
  }

  if (!meldunek) {
    return (
      <Container maxW="6xl" py={6}>
        <Button
          leftIcon={<FiArrowLeft />}
          onClick={() =>
            router.history.push(`/WMS/production-orders/${orderId}`)
          }
          variant="outline"
          mb={4}
        >
          Wróć do zlecenia
        </Button>
        <Text color="red.500">Meldunek nie został znaleziony.</Text>
      </Container>
    )
  }

  const extraDataEntries = parseExtraData(product?.extra_data)

  return (
    <Container maxW="7xl" py={{ base: 4, md: 6 }}>
      {/* Header Bar */}
      <Stack
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "start", md: "center" }}
        mb={6}
        spacing={4}
      >
        <HStack spacing={3}>
          <Button
            leftIcon={<FiArrowLeft />}
            size="sm"
            variant="ghost"
            onClick={() =>
              router.history.push(`/WMS/production-orders/${orderId}`)
            }
          >
            Wróć do zlecenia
          </Button>
          <Heading size="md" fontWeight="bold">
            Karta Partii Wyrobu: {meldunek.number}
          </Heading>
        </HStack>

        <HStack spacing={2}>
          <Tooltip label="Derewo technologii produktu" hasArrow>
            <IconButton
              size="sm"
              aria-label="Derewo technologii produktu"
              icon={<FiShare2 />}
              colorScheme="purple"
              variant="outline"
              onClick={() => {
                const techId = order?.technology_id
                const query = techId
                  ? `?root_technology_id=${techId}`
                  : outputProductId
                    ? `?root_product_id=${outputProductId}`
                    : ""
                router.history.push(`/WMS/technologies/tree${query}`)
              }}
            />
          </Tooltip>

          <Tooltip label="Mapa / Derewo produktu" hasArrow>
            <IconButton
              size="sm"
              aria-label="Mapa produktu"
              icon={<FiGitBranch />}
              colorScheme="teal"
              variant="outline"
              onClick={() => {
                const query = outputProductId
                  ? `?product_id=${outputProductId}`
                  : ""
                router.history.push(`/WMS/production-orders/chain${query}`)
              }}
            />
          </Tooltip>

          <PrintLanguageSelect
            onPrintPL={handlePrintPdfPL}
            onPrintEN={handlePrintPdfEN}
            size="sm"
          />
        </HStack>
      </Stack>

      <Stack spacing={6}>
        {/* Paszport Partii & Barcode Header Card */}
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardHeader pb={2}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
              <HStack spacing={2}>
                <Icon as={FiBox} color="blue.500" w={5} h={5} />
                <Heading size="sm">Paszport Partii Wyrobu Gotowego</Heading>
              </HStack>
              <Badge colorScheme="blue" fontSize="xs" px={2} py={1}>
                Paleta / Sekwencja #{meldunek.seq_no}
              </Badge>
            </Flex>
          </CardHeader>

          <CardBody pt={2}>
            <Grid
              templateColumns={{ base: "1fr", md: "2fr 1fr" }}
              gap={6}
              alignItems="center"
            >
              <Stack spacing={3}>
                <Box>
                  <Text
                    fontSize="xs"
                    color={mutedText}
                    textTransform="uppercase"
                    fontWeight="bold"
                  >
                    Wyrób Gotowy
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color="blue.600">
                    {product?.name ?? "Wyrób zalogowany"}
                  </Text>
                  <HStack spacing={4} mt={1}>
                    <Text fontSize="sm" fontFamily="mono">
                      SKU: <strong>{product?.sku ?? "—"}</strong>
                    </Text>
                    {product?.EAN && (
                      <Text fontSize="sm" fontFamily="mono">
                        EAN: <strong>{product.EAN}</strong>
                      </Text>
                    )}
                  </HStack>
                </Box>

                <SimpleGrid
                  columns={{ base: 2, sm: 4 }}
                  gap={3}
                  bg={grayBg}
                  p={3}
                  borderRadius="md"
                >
                  <Box>
                    <Text fontSize="xs" color={mutedText}>
                      Ilość wyprodukowana
                    </Text>
                    <Text fontSize="md" fontWeight="bold">
                      {formatNum(meldunek.produced_qty)}{" "}
                      {product?.unit ?? "szt"}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="xs" color={mutedText}>
                      Data meldunku
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {formatDate(meldunek.created_at)}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="xs" color={mutedText}>
                      Magazyn / Lokalizacja
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {warehouseMap[meldunek.output_warehouse_id]?.code ??
                        warehouseMap[order?.output_warehouse_id ?? ""]?.code ??
                        "—"}{" "}
                      {meldunek.output_location_code
                        ? `(${meldunek.output_location_code})`
                        : ""}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontSize="xs" color={mutedText}>
                      Metoda wyceny
                    </Text>
                    <Badge colorScheme="purple" fontSize="xs">
                      {meldunek.costing_method ?? "FIFO"}
                    </Badge>
                  </Box>
                </SimpleGrid>
              </Stack>

              {/* Barcode Display */}
              <Flex
                direction="column"
                align="center"
                justify="center"
                p={4}
                bg={barcodeBg}
                borderWidth="1px"
                borderRadius="md"
              >
                <Text fontSize="xs" color={mutedText} mb={2} fontWeight="bold">
                  Kod Kreskowy Partii
                </Text>
                <Box p={2} bg="white" borderRadius="sm">
                  <BarcodeSvg value={meldunek.number} height={60} />
                </Box>
                <Text fontSize="xs" fontFamily="mono" mt={1}>
                  {meldunek.number}
                </Text>
              </Flex>
            </Grid>
          </CardBody>
        </Card>

        {/* Technical Specification Grid */}
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardHeader pb={2}>
            <HStack spacing={2}>
              <Icon as={FiTag} color="teal.500" w={5} h={5} />
              <Heading size="sm">Specyfikacja Techniczna Wyrobu</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            {extraDataEntries.length === 0 ? (
              <Text fontSize="sm" color={mutedText}>
                Brak przypisanych dodatkowych parametrów technicznych dla tego
                produktu.
              </Text>
            ) : (
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                {extraDataEntries.map(([key, val]) => (
                  <Box
                    key={key}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    bg={grayBg}
                  >
                    <Text
                      fontSize="xs"
                      color={mutedText}
                      fontWeight="bold"
                      textTransform="uppercase"
                    >
                      {key}
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" mt={1}>
                      {typeof val === "string" ? val : JSON.stringify(val)}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </CardBody>
        </Card>

        {/* Consumed Materials & Components Table */}
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardHeader pb={2}>
            <HStack spacing={2}>
              <Icon as={FiLayers} color="orange.500" w={5} h={5} />
              <Heading size="sm">Faktycznie Zużyte Surowce i Materiały</Heading>
            </HStack>
          </CardHeader>
          <CardBody px={0}>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead bg={grayBg}>
                  <Tr>
                    <Th>Materiał / Surowiec</Th>
                    <Th>SKU</Th>
                    <Th>Lokalizacja</Th>
                    <Th>Partia Surowca</Th>
                    <Th isNumeric>Plan (Normatyw)</Th>
                    <Th isNumeric>Zużyто (Wykonanie)</Th>
                    <Th isNumeric>Odpad / Braki</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(meldunek.items ?? []).map((item: any) => {
                    const pItem = productMap[item.product_id]
                    return (
                      <Tr key={item.id}>
                        <Td fontWeight="medium">
                          {pItem?.name ?? item.product_id}
                        </Td>
                        <Td fontFamily="mono" fontSize="xs">
                          {pItem?.sku ?? "—"}
                        </Td>
                        <Td>{item.location_code || "—"}</Td>
                        <Td fontFamily="mono" fontSize="xs">
                          {item.warehouse_lot_id
                            ? `LOT-${item.warehouse_lot_id.slice(0, 8)}`
                            : "—"}
                        </Td>
                        <Td isNumeric>{formatNum(item.planned_qty)}</Td>
                        <Td isNumeric fontWeight="bold" color="blue.600">
                          {formatNum(item.used_qty)}
                        </Td>
                        <Td
                          isNumeric
                          color={item.scrap_qty > 0 ? "red.500" : undefined}
                        >
                          {formatNum(item.scrap_qty)}
                        </Td>
                      </Tr>
                    )
                  })}
                  {(!meldunek.items || meldunek.items.length === 0) && (
                    <Tr>
                      <Td colSpan={7} textAlign="center" color={mutedText}>
                        Brak zarejestrowanych materiałów zużytych
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>

        {/* Costing Summary Card */}
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardHeader pb={2}>
            <HStack spacing={2}>
              <Icon as={FiCpu} color="green.500" w={5} h={5} />
              <Heading size="sm">
                Kalkulacja Kosztów Partii i Sobiecości
              </Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
              <Box p={3} borderWidth="1px" borderRadius="md" bg={grayBg}>
                <Text fontSize="xs" color={mutedText}>
                  Koszt materiałowy netto
                </Text>
                <Text fontSize="md" fontWeight="bold">
                  {formatMoney(meldunek.material_cost_netto)} zł
                </Text>
              </Box>

              <Box p={3} borderWidth="1px" borderRadius="md" bg={grayBg}>
                <Text fontSize="xs" color={mutedText}>
                  Koszt usług / narzutu
                </Text>
                <Text fontSize="md" fontWeight="bold">
                  {formatMoney(meldunek.service_cost_netto)} zł
                </Text>
              </Box>

              <Box p={3} borderWidth="1px" borderRadius="md" bg={grayBg}>
                <Text fontSize="xs" color={mutedText}>
                  Łączny koszt netto partii
                </Text>
                <Text fontSize="md" fontWeight="bold" color="green.600">
                  {formatMoney(meldunek.total_cost_netto)} zł
                </Text>
              </Box>

              <Box
                p={3}
                borderWidth="2px"
                borderColor="green.400"
                borderRadius="md"
                bg={greenBg}
              >
                <Text fontSize="xs" color="green.700" fontWeight="bold">
                  Jednostkowy koszt wytworzenia
                </Text>
                <Text fontSize="xl" fontWeight="extrabold" color="green.600">
                  {formatMoney(meldunek.unit_cost_netto)} zł /{" "}
                  {product?.unit ?? "szt"}
                </Text>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>
      </Stack>
    </Container>
  )
}

function useMemoMap(products: ProductResponceDTO[]) {
  const map: Record<string, ProductResponceDTO> = {}
  for (const p of products) {
    map[p.id] = p
  }
  return map
}

function useMemoWarehouseMap(warehouses: any[]) {
  const map: Record<string, any> = {}
  for (const w of warehouses) {
    map[w.id] = w
  }
  return map
}

function parseExtraData(extraData: any): Array<[string, any]> {
  if (!extraData) return []
  if (typeof extraData === "object" && !Array.isArray(extraData)) {
    return Object.entries(extraData)
  }
  if (typeof extraData === "string") {
    try {
      const parsed = JSON.parse(extraData)
      if (typeof parsed === "object" && !Array.isArray(parsed)) {
        return Object.entries(parsed)
      }
    } catch {
      return []
    }
  }
  return []
}

function formatNum(val?: number): string {
  if (val === undefined || val === null) return "0"
  return val.toLocaleString("pl-PL", { maximumFractionDigits: 3 })
}

function formatMoney(val?: number): string {
  if (val === undefined || val === null) return "0,00"
  return val.toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "—"
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return dateStr
  return d.toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}
